import axios from 'axios';
import produce from 'immer';
import { isEmpty, uniqBy } from 'lodash';

import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';
import { deletePosts } from 'redux-store/reducers/instagramPostsDeleteReducer';

import { API, get } from 'utils/api';
import { logError } from 'utils/log';
import { snakeCaseKeys } from 'utils/keys';
import { createReducer } from 'utils/redux';
import { getPostCodeFromUrl, getPostsByAvailability, getTikTokVideoCodeFromUrl } from 'utils/posts';
import { getTikTokThumbnailUrl } from 'utils/tiktok-thumbnail';
import { Querystring } from 'utils/url';
import { getInstagramThumbnailUrl } from 'utils/content/thumbnails';
import {
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  getYoutubeVideoThumbnail,
  isInstagramChannel,
  isTiktokChannel
} from 'utils/channels';
import { getEmbedUrl } from 'components/tiles/standard/utils/tile-standard';
import { getKeywords, getSelectedTaxonomyIds } from 'modules/discovery/creators/utils/creators';
import { getPromiseContext } from 'modules/influencers/utils/promise-handler';

import type { Action, DiscoveryCreatorsFilters, ImageGridImage, Post, PostAlike, ThunkAction, TypedAction } from 'types';
import type { RecentPostsDataType } from 'modules/influencers/types/RecentPosts.type';
import type { PromiseContext } from 'modules/influencers/utils/promise-handler';

const SET_DATA = 'influencerRecentPosts/setResponseData';
const SET_LOADING_STATE = 'influencerRecentPosts/setLoadingState';
const RESET_DATA = 'influencerRecentPosts/resetData';

export const NUM_POSTS_TO_LOAD: number = 9;

type State = {
  recentPostsData: {
    [x: number]: RecentPostsDataType
  }
};

const initialState: State = {
  recentPostsData: {}
};

type SetDataActionPayload = {
  profileId: number,
  data: Array<ImageGridImage>,
  page: number,
  hasMorePages: boolean,
  channel: string
};

function setData(
  profileId: number,
  data: Array<ImageGridImage>,
  page: number,
  hasMorePages: boolean,
  channel: string
): TypedAction<SetDataActionPayload> {
  return {
    type: SET_DATA,
    payload: {
      profileId,
      data,
      page,
      hasMorePages,
      channel
    }
  };
}


type SetLoadingStateActionPayload = {
  profileIds: Array<number>,
  isLoading: boolean,
  channel: string
};

function setLoadingState(profileIds: Array<number>, isLoading: boolean, channel: string): TypedAction<SetLoadingStateActionPayload> {
  return {
    type: SET_LOADING_STATE,
    payload: {
      profileIds,
      isLoading,
      channel
    }
  };
}

function resetData(profileId: number): Action {
  return {
    type: RESET_DATA,
    payload: {
      profileId
    }
  };
}

let fetchInfluencerRecentPostsCancelToken;

function buildQueryForSearch(
  ids: Array<number>,
  channelType: string,
  filters: DiscoveryCreatorsFilters,
  page: number = 1
): string {
  const keywords = getKeywords(filters.keywords);

  const params = {
    itemsPerPage: NUM_POSTS_TO_LOAD,
    profileIds: ids.join(','),
    channelType,
    page,
    includedKeywords: keywords.included || undefined,
    excludedKeywords: keywords.excluded || undefined,
    includedKeywordsOperator: filters.keywords.matchMode.toUpperCase(),
    categoriesIds: getSelectedTaxonomyIds(filters.categories.items).join(',') || undefined,
    categoriesOperator: filters.categories.matchMode.toUpperCase()
  };

  return Querystring.objectToString(snakeCaseKeys(params));
}

function buildQueryForLists(ids: Array<number>, channelType: string, page: number = 1): string {
  const params = {
    itemsPerPage: NUM_POSTS_TO_LOAD,
    profileIds: ids.join(','),
    channelType,
    page
  };

  return Querystring.objectToString(snakeCaseKeys(params));
}

const CancelError = new Error('Cancelled Fetch');

type PostFetcher = (url: string, size: string, post: PostAlike) => Promise<string> | string;

export function fetchInfluencersRecentPosts(
  ids: Array<number>,
  channelType: string,
  filters: DiscoveryCreatorsFilters,
  page: number = 1,
  isInitialFetchCancelable: boolean = true,
  isResourceFetchCancelable: boolean = true,
  postsLeftToFetch: number = NUM_POSTS_TO_LOAD,
  activeContext: PromiseContext | undefined = undefined
): ThunkAction {
  return async (dispatch, getState) => {
    const consents = getState().consentReducer.consents;
    const currentPromiseContext = activeContext ?? getPromiseContext(isResourceFetchCancelable);

    // handle cancellation in case of repeated request
    if (fetchInfluencerRecentPostsCancelToken) {
      fetchInfluencerRecentPostsCancelToken.cancel();
    }
    fetchInfluencerRecentPostsCancelToken = axios.CancelToken.source();

    try {
      if (ids.length === 0) {
        return;
      }

      if (page === 1) {
        // cleanup if fetching first page
        ids.forEach(id => dispatch(resetData(id)));
      }

      let url;

      if (isEmpty(filters)) {
        url = API.END_POINTS.INFLUENCERS.getInfluencersRecentPosts(
          buildQueryForLists(ids, channelType, page)
        );
      } else {
        url = API.END_POINTS.INFLUENCERS.getInfluencersRecentPosts(
          buildQueryForSearch(ids, channelType, filters, page)
        );
      }

      dispatch(setLoadingState(ids, true, channelType));

      // cancel only if fetching first page
      const cancelToken = (isInitialFetchCancelable && page === 1)
        ? fetchInfluencerRecentPostsCancelToken.token
        : undefined;

      const result = await get(
        url,
        { cancelToken },
        { suppressGlobalNotification: true }
      );

      // fetch posts data and filter unavailable posts
      let getPostByUrl: PostFetcher = getYoutubeVideoThumbnail;

      if (isInstagramChannel(channelType)) {
        getPostByUrl = getInstagramThumbnailUrl;
      }

      if (isTiktokChannel(channelType)) {
        getPostByUrl = getTikTokThumbnailUrl;
      }

      const postsByUsers = result.data;

      for (let index = 0; index < ids.length; index++) {
        if (currentPromiseContext.isCancelled) {
          throw CancelError;
        }

        const profileId = ids[index];
        const entry = postsByUsers.find(user => user.profileId === profileId);
        if (!entry) {
          continue;
        }
        const { posts } = entry;
        const hasMorePages = posts.meta.currentPage < posts.meta.pagesTotal;

        // for each user in the list get available posts and set them asynchronously
        try {
          let postsToUse: Array<Post> = posts.data;
          let postsToDrop: Array<Post> = [];

          if (consents[channelType]) {
            const {
              unavailablePosts,
              availablePosts
            } = await getPostsByAvailability(posts.data, posts.data.length, getPostByUrl, undefined, 'm'); // eslint-disable-line no-await-in-loop

            postsToUse = availablePosts;
            postsToDrop = unavailablePosts;
          }


          if (currentPromiseContext.isCancelled) {
            throw CancelError;
          }

          const postsWithTypeAndEmbedURL = postsToUse.map(post => ({
            ...post,
            imageUrl: post.assetUrl || post.imageUrl,
            embedUrl: getEmbedUrl(post.url, channelType),
            type: channelType
          }));

          const hasNoPosts = postsWithTypeAndEmbedURL.length === 0;
          const postsLeftToFetchForNextBatch = postsLeftToFetch - postsWithTypeAndEmbedURL.length;
          const hasNotEnoughPosts = postsLeftToFetchForNextBatch > 0;
          const shouldLoadNextPage = hasNoPosts || hasNotEnoughPosts;

          if (shouldLoadNextPage && hasMorePages && postsToUse.length > 0) {
            // if no available posts for current user exists will try to continue loading
            dispatch(fetchInfluencersRecentPosts(
              [Number(profileId)],
              channelType,
              filters,
              page + 1,
              false,
              false,
              postsLeftToFetchForNextBatch,
              currentPromiseContext
            ));
          }

          if (postsWithTypeAndEmbedURL.length > 0) {
            dispatch(setData(Number(profileId), postsWithTypeAndEmbedURL, page, hasMorePages, channelType));
          }

          if (postsWithTypeAndEmbedURL.length === 0 && postsToUse.length === 0) {
            dispatch(setData(Number(profileId), [], page, false, channelType));
          }

          // drop unavailable
          if (channelType === CHANNEL_TYPE_INSTAGRAM && !isEmpty(postsToDrop)) {
            const postsCodes = postsToDrop.map(post => getPostCodeFromUrl(post.url));
            dispatch(deletePosts(postsCodes, 'instagram'));
          }

          if (channelType === CHANNEL_TYPE_TIKTOK && !isEmpty(postsToDrop)) {
            const postsCodes = postsToDrop.map(post => getTikTokVideoCodeFromUrl(post.url));
            dispatch(deletePosts(postsCodes, 'tiktok'));
          }
        } catch (err) {
          if (err === CancelError) {
            // totally fine to skip
            return;
          }

          logError(err);
        }
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }

      if (error === CancelError) {
        return;
      }

      logError(error);
    }
  };
}

export function setLoadingStateReducer(
  state: State,
  {
    profileIds,
    isLoading,
    channel
  }: SetLoadingStateActionPayload
): State {
  const newRecentPosts = {
    ...state.recentPostsData
  };

  profileIds.forEach(id => {
    newRecentPosts[id] = {
      ...newRecentPosts[id] ?? {},
      isLoading,
      channel
    };
  });

  return {
    ...state,
    recentPostsData: newRecentPosts
  };
}

export function setDataReducer(
  state: State,
  {
    profileId,
    data,
    page,
    hasMorePages,
    channel
  }: SetDataActionPayload
): State {
  if (channel !== state.recentPostsData[profileId]?.channel) {
    // currently loading channel is different from actual loaded data
    // might happen if post validation took too long and meanwhile user switched tabs
    // so post validated after we've triggered fetching new channels
    // and now trying to overwrite data with broken state
    return state;
  }

  // reset array on first page or concat otherwise
  const newPosts: Array<ImageGridImage> = (page === 1)
    ? data
    : (state.recentPostsData[profileId]?.posts || []).concat(data);

  const newStateForProfile: RecentPostsDataType = {
    lastFetchedPage: page,
    hasMorePages,
    channel,
    isLoading: false,
    posts: uniqBy(newPosts, 'id')
  };

  return ({
    ...state,
    recentPostsData: {
      ...state.recentPostsData,
      [profileId]: newStateForProfile
    }
  });
}

export default createReducer<State, any>('influencerRecentPosts', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [SET_LOADING_STATE]: setLoadingStateReducer,
  [SET_DATA]: setDataReducer,
  [RESET_DATA]: (state, { profileId }) => produce(state, (draft) => {
    // eslint-disable-next-line no-param-reassign
    delete draft.recentPostsData[profileId];
  })
});
